import { OnboardingQuestionConfig, UserPreferences, UserPreferencesRequest } from '@Models';

const snakeToCamel = (s: string): string => s.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase());

const escapeRegex = (s: string): string => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

type StringField = 'preferredName' | 'writingStyle' | 'businessRules' | 'userInstructions';

const FIELD_MAP: Record<string, StringField> = {
  preferred_name: 'preferredName',
  writing_style: 'writingStyle',
  business_rules: 'businessRules',
  user_instructions: 'userInstructions',
};

export const mapAnswersToRequest = (
  answers: Record<string, string | string[]>,
  questions: OnboardingQuestionConfig[],
): UserPreferencesRequest => {
  const request: UserPreferencesRequest = {
    preferredName: '',
    writingStyle: '',
    combinationsToFilter: null,
    businessRules: '',
    userInstructions: '',
  };
  const combos: Record<string, string[]> = {};

  questions.forEach((q) => {
    const value = answers[q.key];
    if (!value) return;

    const strValue = Array.isArray(value) ? value.join(', ') : value;

    if (q.targetField === 'combinations_to_filter' && q.targetKey) {
      combos[q.targetKey] = Array.isArray(value) ? value : [value];
    } else {
      const finalValue = q.template ? q.template.replace('{value}', strValue) : strValue;
      const key = FIELD_MAP[q.targetField];
      if (key) {
        request[key] = finalValue;
      }
    }
  });

  if (Object.keys(combos).length > 0) {
    request.combinationsToFilter = combos;
  }

  return request;
};

export const mapPreferencesToAnswers = (
  prefs: UserPreferences,
  questions: OnboardingQuestionConfig[],
): Record<string, string | string[]> => {
  const answers: Record<string, string | string[]> = {};

  questions.forEach((q) => {
    if (q.targetField === 'combinations_to_filter' && q.targetKey) {
      const camelKey = snakeToCamel(q.targetKey);
      const vals = prefs.combinationsToFilter?.[camelKey];
      if (vals?.length) {
        answers[q.key] = vals;
      }
    } else {
      const key = FIELD_MAP[q.targetField];
      const stored = key ? prefs[key] : undefined;
      if (typeof stored === 'string' && stored) {
        if (q.template) {
          const parts = q.template.split('{value}');
          const pattern = parts.map(escapeRegex).join('(.+)');
          const match = stored.match(new RegExp(`^${pattern}$`));
          if (match) {
            const [, extracted] = match;
            answers[q.key] = extracted;
          }
        } else {
          answers[q.key] = stored;
        }
      }
    }
  });

  return answers;
};
